/**
 * Small building blocks of the Salvation Team console: the capability gate, the page header, the status badge and
 * the Lead / Backup cards (who is responsible now is highlighted; ownership never changes here).
 */

import type { ReactNode } from 'react';
import { IconLock, IconAlertTriangle, IconUserCheck } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { Badge, Group, Paper, Stack, Text, Title, Tooltip } from '@/components/ui';
import type { SalvationPersonRef, SalvationStatusItem } from '@actschurch/ccms-sdk';
import { useSalvationPermissions, type SalvationPermissions } from '@/hooks/salvation/useSalvationPermissions';

// ---------------------------------------------------------------- gate

interface SalvationGateProps {
  allow: (p: SalvationPermissions) => boolean;
  children: ReactNode;
}

/** Capability gate for Salvation pages (role OR right, like the API). */
export function SalvationGate({ allow, children }: SalvationGateProps) {
  const perms = useSalvationPermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You don't have permission to open this page. Ask a Salvation Team admin for access."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------- header

interface SalvationPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Shown above the title (for example a back link). */
  above?: ReactNode;
  /** Shown under the title (for example status badges). */
  below?: ReactNode;
}

export function SalvationPageHeader({ title, description, actions, above, below }: SalvationPageHeaderProps) {
  return (
    <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
      <div style={{ minWidth: 0, flex: 1 }}>
        {above}
        <Title order={2}>{title}</Title>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
        {below}
      </div>
      {actions && (
        <Group gap="sm" wrap="wrap">
          {actions}
        </Group>
      )}
    </Group>
  );
}

// ---------------------------------------------------------------- badges

/** The person's status in the lookup colour (falls back to the id when lookups are still loading). */
export function StatusBadge({ statusId, statuses, size = 'sm' }: { statusId: number; statuses: Map<number, SalvationStatusItem>; size?: 'xs' | 'sm' | 'md' }) {
  const s = statuses.get(statusId);
  return (
    <Badge size={size} variant="light" color={s?.color ?? 'gray'} style={{ whiteSpace: 'nowrap' }}>
      {s?.name ?? `Status ${statusId}`}
    </Badge>
  );
}

/** "Past the deadline" / "Backup responsible" / "Needs review" flags of a person. */
export function PersonFlags({ isOverdue, backupIsResponsible, reviewNeeded, isEscalated }: {
  isOverdue?: boolean;
  backupIsResponsible?: boolean;
  reviewNeeded?: boolean;
  isEscalated?: boolean;
}) {
  if (!isOverdue && !backupIsResponsible && !reviewNeeded && !isEscalated) return null;
  return (
    <Group gap={4} wrap="wrap">
      {isEscalated && <Badge size="xs" color="red" variant="filled">Escalated</Badge>}
      {isOverdue && !isEscalated && <Badge size="xs" color="red" variant="light">Past the deadline</Badge>}
      {backupIsResponsible && <Badge size="xs" color="orange" variant="light">Backup responsible</Badge>}
      {reviewNeeded && <Badge size="xs" color="grape" variant="light">Needs review</Badge>}
    </Group>
  );
}

// ---------------------------------------------------------------- Lead / Backup

function RoleCard({ label, person, responsible, reason }: { label: string; person?: SalvationPersonRef | null; responsible: boolean; reason?: string }) {
  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        flex: 1,
        minWidth: 180,
        borderColor: responsible ? 'var(--mantine-color-green-6)' : undefined,
        borderWidth: responsible ? 2 : undefined,
      }}
    >
      <Stack gap={2}>
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            {label}
          </Text>
          {responsible && (
            <Tooltip label={reason ?? 'Responsible now'} withArrow>
              <Badge size="xs" color="green" variant="light" leftSection={<IconUserCheck size={12} />}>
                Responsible now
              </Badge>
            </Tooltip>
          )}
        </Group>
        {person ? (
          <Group gap={6} wrap="nowrap">
            <Text fw={600}>{person.displayName}</Text>
            {!person.isActive && (
              <Tooltip label="No longer active on the team" withArrow>
                <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
              </Tooltip>
            )}
          </Group>
        ) : (
          <Text c="dimmed" fs="italic">
            Not assigned yet
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/** The permanent Lead and Backup, with "responsible now" on whoever should act. */
export function LeadBackupCards({ lead, backup, responsible, reason }: {
  lead?: SalvationPersonRef | null;
  backup?: SalvationPersonRef | null;
  responsible?: SalvationPersonRef | null;
  reason?: string;
}) {
  const responsibleId = responsible?.teamMemberID;
  return (
    <Group gap="sm" align="stretch" wrap="wrap">
      <RoleCard label="Lead" person={lead} responsible={!!lead && lead.teamMemberID === responsibleId} reason={reason} />
      <RoleCard label="Backup" person={backup} responsible={!!backup && backup.teamMemberID === responsibleId} reason={reason} />
    </Group>
  );
}
